import React from "react";
import { CAT_COLOR, RISK_CFG } from "../../../data/riskAssessmentData";
import type {
    Question,
    RiskRating,
} from "../../../types/risk-assessment.types";
import { S, primary } from "./styles";

interface StepTwoProps {
  grouped: Record<string, Question[]>;
  questions: Question[];
  answers: Record<string, boolean>;
  setAnswers: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  answered: number;
  allDone: boolean;
  activeSection: number;
  setActiveSection: (s: number) => void;
  liveRisk: RiskRating;
  liveRc: (typeof RISK_CFG)[RiskRating];
  hoveredInfo: string | null;
  setHoveredInfo: (v: string | null) => void;
  setStep: (s: number) => void;
}

const StepTwo: React.FC<StepTwoProps> = ({
  grouped,
  questions,
  answers,
  setAnswers,
  answered,
  allDone,
  activeSection,
  setActiveSection,
  liveRisk,
  liveRc,
  hoveredInfo,
  setHoveredInfo,
  setStep,
}) => {
  const sections = Object.entries(grouped);
  const sectionLabels = sections.map(([cat]) => {
    if (cat === "Designated Service Risk") return "Designated Service";
    if (cat === "Client Risk") return "Client Risk";
    if (cat === "Delivery Channel Risk") return "Delivery Channel";
    if (cat === "Country Risk") return "Country / Jurisdiction";
    return cat;
  });
  const sectionLetters = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];
  const clampedSection = Math.min(activeSection, sections.length - 1);
  const [currentCat, currentQs] = sections[clampedSection] || ["", []];
  const currentCatColor = CAT_COLOR[currentCat] || "#64748b";

  let qOffset = 0;
  for (let s = 0; s < clampedSection; s++) qOffset += sections[s][1].length;

  return (
    <div>
      <div
        style={{
          padding: "16px 24px",
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        {/* Section pill tabs */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 16,
          }}
        >
          {sections.map(([cat, qs], idx) => {
            const secAnswered = qs.filter(
              (q: Question) => answers[q.id] !== undefined,
            ).length;
            const secDone = secAnswered === qs.length;
            const isActive = idx === clampedSection;
            return (
              <button
                key={cat}
                onClick={() => setActiveSection(idx)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 14px",
                  borderRadius: 20,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                  transition: "all 0.2s",
                  border: isActive ? "2px solid #1B3A6B" : "1px solid #E2E8F0",
                  background: isActive
                    ? "#1B3A6B"
                    : secDone
                      ? "#F0FDF4"
                      : "#fff",
                  color: isActive ? "#fff" : secDone ? "#16A34A" : "#475569",
                }}
              >
                {sectionLetters[idx]}. {sectionLabels[idx]}
                {secDone && <span style={{ fontSize: 11 }}>✓</span>}
              </button>
            );
          })}
        </div>

        {/* Risk badge + progress bar row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: liveRc.bg,
              border: `1px solid ${liveRc.border}`,
              borderRadius: 20,
              padding: "4px 14px",
            }}
          >
            <span style={{ fontSize: 10 }}>
              {liveRisk === "High" ? "🔴" : liveRisk === "Medium" ? "🟠" : "🟢"}
            </span>
            <span
              style={{
                color: liveRc.color,
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {answered > 0 ? liveRc.label.toUpperCase() : "AWAITING"}
            </span>
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  color: "#94A3B8",
                  fontSize: 11,
                  fontWeight: 500,
                }}
              >
                Progress
              </span>
              <span
                style={{
                  color: "#1E293B",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {answered}/{questions.length} (
                {questions.length > 0
                  ? Math.round((answered / questions.length) * 100)
                  : 0}
                %)
              </span>
            </div>
            <div
              style={{
                height: 6,
                borderRadius: 4,
                background: "#E2E8F0",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  borderRadius: 4,
                  background: "linear-gradient(90deg, #1B3A6B, #2E5FA3)",
                  width: `${questions.length > 0 ? (answered / questions.length) * 100 : 0}%`,
                  transition: "width 0.3s",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div style={S.body}>
        {/* Section heading */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20,
            paddingBottom: 14,
            borderBottom: `2px solid ${currentCatColor}30`,
          }}
        >
          <div
            style={{
              width: 4,
              height: 22,
              borderRadius: 2,
              background: currentCatColor,
            }}
          />
          <h3
            style={{
              margin: 0,
              color: "#1E293B",
              fontSize: 16,
              fontWeight: 800,
            }}
          >
            {sectionLetters[clampedSection]}. {sectionLabels[clampedSection]}
          </h3>
        </div>

        {/* Question rows */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {currentQs.map((q: Question, qi: number) => {
            const qNum = qOffset + qi + 1;
            const isYes = answers[q.id] === true;
            const isNo = answers[q.id] === false;
            const isAnswered = answers[q.id] !== undefined;
            return (
              <div
                key={q.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "14px 18px",
                  background: isYes
                    ? "#FEF2F2"
                    : isNo
                      ? "#F0FDF4"
                      : qi % 2 === 0
                        ? "#FAFBFC"
                        : "#fff",
                  borderRadius: 10,
                  border: isYes
                    ? "1px solid #FECACA"
                    : isNo
                      ? "1px solid #BBF7D0"
                      : "1px solid transparent",
                  transition: "all 0.2s",
                }}
              >
                {/* Question number */}
                <span
                  style={{
                    color: isAnswered
                      ? isYes
                        ? "#DC2626"
                        : "#16A34A"
                      : "#1B3A6B",
                    fontSize: 13,
                    fontWeight: 700,
                    minWidth: 22,
                    flexShrink: 0,
                  }}
                >
                  {qNum}.
                </span>

                {/* Question text + info icon */}
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 6,
                  }}
                >
                  <p
                    style={{
                      flex: 1,
                      color: "#334155",
                      fontSize: 13.5,
                      margin: 0,
                      lineHeight: 1.55,
                      fontWeight: 400,
                    }}
                  >
                    {q.text}
                  </p>
                  {q.info && (
                    <div
                      style={{
                        position: "relative",
                        flexShrink: 0,
                        marginTop: 2,
                      }}
                      onMouseEnter={() => setHoveredInfo(q.id)}
                      onMouseLeave={() => setHoveredInfo(null)}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          background: "#FEF9C3",
                          color: "#A16207",
                          fontSize: 10,
                          fontWeight: 700,
                          cursor: "default",
                          userSelect: "none",
                        }}
                      >
                        i
                      </span>
                      {hoveredInfo === q.id && (
                        <div
                          style={{
                            position: "absolute",
                            top: "calc(100% + 6px)",
                            right: 0,
                            width: 280,
                            background: "#1E293B",
                            color: "#E2E8F0",
                            fontSize: 12,
                            lineHeight: 1.6,
                            borderRadius: 8,
                            padding: "10px 12px",
                            boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                            zIndex: 50,
                            pointerEvents: "none",
                          }}
                        >
                          {q.info}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Risk badge */}
                <span
                  style={{
                    flexShrink: 0,
                    background: q.rating === "High" ? "#FEF2F2" : "#FFF7ED",
                    color: q.rating === "High" ? "#DC2626" : "#EA580C",
                    borderRadius: 4,
                    padding: "3px 8px",
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  {q.rating === "High" ? "HIGH" : "MED"}
                </span>

                {/* Yes / No buttons */}
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexShrink: 0,
                  }}
                >
                  <button
                    onClick={() => setAnswers((p) => ({ ...p, [q.id]: true }))}
                    style={{
                      padding: "6px 16px",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 700,
                      transition: "all 0.15s",
                      background: isYes ? "#FEE2E2" : "#F8FAFC",
                      border: isYes
                        ? "2px solid #EF4444"
                        : "1.5px solid #E2E8F0",
                      color: isYes ? "#DC2626" : "#64748B",
                    }}
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setAnswers((p) => ({ ...p, [q.id]: false }))}
                    style={{
                      padding: "6px 16px",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 700,
                      transition: "all 0.15s",
                      background: isNo ? "#16A34A" : "#F8FAFC",
                      border: isNo
                        ? "2px solid #16A34A"
                        : "1.5px solid #E2E8F0",
                      color: isNo ? "#fff" : "#64748B",
                    }}
                  >
                    No
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Note for flagged questions */}
        {currentQs.some((q: Question) => q.note && answers[q.id] === true) && (
          <div style={{ marginTop: 14 }}>
            {currentQs
              .filter((q: Question) => q.note && answers[q.id] === true)
              .map((q: Question) => (
                <div
                  key={q.id}
                  style={{
                    padding: "10px 14px",
                    marginBottom: 8,
                    background: "#FFFBEB",
                    border: "1px solid #FDE68A",
                    borderRadius: 8,
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                  }}
                >
                  <span style={{ fontSize: 13, flexShrink: 0 }}>⚠️</span>
                  <p
                    style={{
                      color: "#92400E",
                      fontSize: 12,
                      margin: 0,
                      fontWeight: 500,
                      lineHeight: 1.6,
                    }}
                  >
                    {q.note}
                  </p>
                </div>
              ))}
          </div>
        )}

        {/* AL-351: save indicator removed with draft saving. It reported
            "Draft saved successfully" / "Last saved HH:MM:SS" and advertised
            "Auto-saves every 30s" — all driven by the autosave that no longer
            runs. An assessment is now written once, on completion. */}

        {/* Section navigation */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 18,
            paddingTop: 16,
            borderTop: "1px solid #E2E8F0",
          }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            {clampedSection === 0 ? (
              <button onClick={() => setStep(1)} style={S.ghost}>
                ← Back to Setup
              </button>
            ) : (
              <button
                onClick={() => setActiveSection(clampedSection - 1)}
                style={S.ghost}
              >
                ← Prev
              </button>
            )}
            {clampedSection < sections.length - 1 ? (
              <button
                onClick={() => setActiveSection(clampedSection + 1)}
                style={{
                  background: "#1B3A6B",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 20px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Next →
              </button>
            ) : (
              <button
                onClick={() => setStep(3)}
                disabled={!allDone}
                style={{
                  ...primary(allDone),
                  marginTop: 0,
                  width: "auto",
                  padding: "10px 24px",
                }}
              >
                {allDone
                  ? "Generate Risk Assessment →"
                  : `${questions.length - answered} remaining`}
              </button>
            )}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                color: "#94A3B8",
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              Section {clampedSection + 1} of {sections.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepTwo;
